import { useContext, useEffect, useReducer } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { Capability, UserCapabilities } from './capabilityTypes';
import { executeBatch } from './executeBatch';
import { fetchAndFormatCapabilties } from './fetch-and-format-capabilities';
import { reducer } from './reducer';
import { useDeepMemo } from './useDeepMemo';
import { UserCache } from './UserCache';
import { getCapabilitiesByStatus, getUserId } from './utils';

/**
 * This is for backwards compatibility since there are other places in the codebase
 * that import `fetchAndFormatCapabilties` from this file. I will open a follow-up PR
 * to remove this export once all references are updated.
 */
export { fetchAndFormatCapabilties };

/**
 * Exposed for use by `prefetchCapabilities`. Not intended for direct external use.
 */
export { userCache };

/**
 * The userCache is a singleton that contains a capabilitiesCache and a queuedRequest
 * for each user for which we call `useCapability`. These objects allow us to
 * prevent API calls if we already have the capability data we need. They also
 * allow us to prevent duplicate simultaneous API requests for the same capabilities
 * and to batch up multiple invocations of `useCapability` into a single API request.
 */
const userCache = new UserCache();

/**
 * @param requestedCapabilities An array of user capabilities to be requested
 * @param userUri
 *
 * @returns An object with the requested capabilites, a loading state for when the capabilites are available, and an error state if something goes wrong (default null)
 */
export function useCapability(
  requestedCapabilities: Capability[],
  userUri?: string | number | null,
) {
  const viewer = useContext(ViewerContext);
  const userId = userUri != null ? getUserId(`${userUri}`) : viewer?.user?.id;
  const [state, dispatch] = useReducer(reducer, { loading: false, error: null, ready: false });
  const queuedRequest = userCache.getQueuedRequest(userId);
  const capabilitiesCache = userCache.getCapabilities(userId);
  const requestedCapabilitiesMemo = useDeepMemo(requestedCapabilities);

  useEffect(() => {
    if (!viewer || !userId || !capabilitiesCache || !queuedRequest) {
      return;
    }

    // Determine the status of the requested capabilities
    const { missing, queued } = getCapabilitiesByStatus(
      requestedCapabilitiesMemo,
      capabilitiesCache,
    );

    // If none of the requested capabilities are missing or queued, then dispatch
    // success and get the heck out of here.
    if (!missing.length && !queued.length) {
      dispatch({ type: 'SUCCESS' });
      return;
    }

    // Subscribe for notification when ALL of this hook's requested capabilities
    // are resolved. Subscribers persist across batch flushes so that hooks
    // mounting while a fetch is in-flight get notified when it resolves.
    // A subscriber is only dispatched SUCCESS once every capability it listed
    // is a boolean in the cache (not still 'queued').
    queuedRequest.addSubscriber(dispatch, requestedCapabilitiesMemo);
    dispatch({ type: 'LOADING' });

    // If there are capabilities not yet queued, add them to the batch.
    if (missing.length) {
      missing.forEach((c) => {
        capabilitiesCache.update({ [c]: 'queued' });
      });
      queuedRequest.addCapabilities(missing);

      // Start the batch timer if not already running. All capabilities added
      // within the window (BATCH_DELAY_MS) are fetched in a single request.
      // Unlike the old 1ms setTimeout, the timer is NOT reset when new
      // capabilities arrive — they simply join the current batch.
      queuedRequest.scheduleBatch(() =>
        executeBatch(queuedRequest, capabilitiesCache, {
          userId,
          jwt: viewer.jwt,
          apiUrl: viewer.apiUrl,
        }),
      );
    }
  }, [capabilitiesCache, queuedRequest, requestedCapabilitiesMemo, userId, viewer]);

  // If user not logged in explictly return `false` for the requested capabilities. note: does not cache these values
  const capabilities =
    capabilitiesCache?.getAll() ??
    requestedCapabilities.reduce<UserCapabilities>((caps, cap) => {
      caps[cap] = false;
      return caps;
    }, {});

  return { capabilities, loading: state.loading, error: state.error, ready: state.ready };
}
